/**
 * Non-happy-path UI. An ERP is mostly non-happy-path, so these are first-class components
 * rather than inline ternaries.
 *
 * Four states, all of which every list and detail view must handle:
 *
 *   LOADING    skeleton, shaped like the content it replaces
 *   EMPTY      actionable — says what to do, not just "no data"
 *   FORBIDDEN  surfaces the server's own message and `requiredPermission` verbatim.
 *              The backend writes a specific sentence naming the permission and where to
 *              ask for it; replacing that with "Forbidden" throws away the only thing
 *              that would let the user resolve it themselves.
 *   ERROR      the server's message, plus a retry
 */
import type { ReactNode } from 'react';
import { AlertCircle, Inbox, Lock, RefreshCw, WifiOff } from 'lucide-react';

import { ApiError, isApiError } from '@frontend/api/client';
import { cn } from '@frontend/lib/cn';
import { Button } from './Button';

export function Skeleton({ className }: { className?: string }): JSX.Element {
  return <div className={cn('skeleton', className)} aria-hidden />;
}

export function LoadingRows({ rows = 6, columns = 5 }: { rows?: number; columns?: number }): JSX.Element {
  return (
    <div className="space-y-1 p-4" role="status" aria-label="Loading">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-3">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-5 flex-1', columnIndex === 0 && 'max-w-[220px]')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function LoadingCards({ count = 4 }: { count?: number }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-4" role="status" aria-label="Loading">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className="h-24 w-full rounded-lg" />
      ))}
    </div>
  );
}

export interface EmptyStateProps {
  /** What is missing, in the user's terms. */
  title: string;
  /**
   * Why it is empty AND what to do about it. "No projects assigned — ask your manager to
   * add you to a project" beats "No data" every time; the second one leaves the user stuck.
   */
  description: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  'data-testid': testId,
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-2 px-6 py-12 text-center', className)}
      data-testid={testId ?? 'empty-state'}
    >
      <div className="mb-1 text-content-tertiary">{icon ?? <Inbox aria-hidden className="h-7 w-7" />}</div>
      <h3 className="text-section font-semibold text-content">{title}</h3>
      {/*
        A DIV, not a `<p>`. `description` is a ReactNode and several callers pass a
        paragraph or a list in it — Job architecture and Pay bands both do. A `<p>` inside a
        `<p>` is invalid HTML, and the browser's error recovery CLOSES the outer paragraph
        early, so the rest of the description escapes the styled block and renders unstyled
        underneath it. React logs a hydration warning for the same reason.
      */}
      <div className="max-w-prose text-pretty text-body text-content-secondary">{description}</div>
      {action !== undefined && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function ForbiddenState({
  error,
  className,
}: {
  error: ApiError;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}
      data-testid="403-message"
    >
      <Lock aria-hidden className="h-7 w-7 text-content-tertiary" />
      <h3 className="text-section font-semibold text-content">You do not have access to this</h3>
      {/* The server's sentence, unedited. It names the permission and where to ask for it. */}
      <p className="max-w-prose text-pretty text-body text-content-secondary">{error.message}</p>
      {error.requiredPermission !== null && (
        <p className="text-body text-content-secondary">
          Required permission: <code className="code-ref">{error.requiredPermission}</code>
          {error.heldScope !== null && (
            <>
              {' · '}you hold scope <code className="code-ref">{error.heldScope}</code>
            </>
          )}
        </p>
      )}
      {error.requiredPermissionDescription !== null && (
        <p className="max-w-prose text-body text-content-secondary">{error.requiredPermissionDescription}</p>
      )}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}): JSX.Element {
  const apiError = isApiError(error) ? error : null;

  if (apiError !== null && apiError.kind === 'FORBIDDEN') {
    return <ForbiddenState error={apiError} className={className} />;
  }

  const isOffline = apiError !== null && apiError.kind === 'NETWORK';
  const message =
    apiError?.message ??
    (error instanceof Error
      ? error.message
      : 'Something went wrong and the details were lost. Reload the page; if it happens again, tell your administrator what you were doing.');

  return (
    <div
      className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}
      data-testid="error-state"
    >
      {isOffline ? (
        <WifiOff aria-hidden className="h-7 w-7 text-content-tertiary" />
      ) : (
        <AlertCircle aria-hidden className="h-7 w-7 text-error" />
      )}
      <h3 className="text-section font-semibold text-content">
        {isOffline ? 'No connection to the server' : 'That did not work'}
      </h3>
      <p className="max-w-prose text-pretty text-body text-content-secondary">{message}</p>
      {apiError !== null && apiError.issues.length > 0 && (
        <ul className="max-w-prose space-y-0.5 text-left text-body text-content-secondary">
          {apiError.issues.map((issue) => (
            <li key={`${issue.field}:${issue.message}`}>
              <span className="font-medium text-content">{issue.field}</span>: {issue.message}
            </li>
          ))}
        </ul>
      )}
      {onRetry !== undefined && (
        <Button variant="secondary" size="sm" leadingIcon={<RefreshCw className="h-3.5 w-3.5" />} onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/**
 * The standard wrapper around a query. Handles all four states in one place so screens
 * do not each invent their own.
 */
export function QueryState<T>({
  query,
  children,
  loading,
  empty,
  isEmpty,
}: {
  query: {
    data: T | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  };
  children: (data: T) => ReactNode;
  loading?: ReactNode;
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
}): JSX.Element {
  if (query.isLoading) return <>{loading ?? <LoadingRows />}</>;
  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  if (query.data === undefined) {
    return (
      <ErrorState
        error={new Error('The server returned an empty response where data was expected.')}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  if (isEmpty !== undefined && isEmpty(query.data) && empty !== undefined) return <>{empty}</>;
  return <>{children(query.data)}</>;
}
